<template>
    <slot name="before" />

    <template v-for="section in filteredSections">
        <LazyFlexibleSectionEventBlock
            v-if="section.__typename === 'flexibleSections_eventBlock_BlockType'"
            :key="section.id"
            v-bind="section"
        />

        <LazyFlexibleSectionCollectionsBlock
            v-if="section.__typename === 'flexibleSections_collectionsBlock_BlockType'"
            :key="section.id"
            v-bind="section"
        />

        <LazyFlexibleSectionArtistBlock
            v-if="section.__typename === 'flexibleSections_artistBlock_BlockType'"
            :key="section.id"
            v-bind="section"
        />

        <LazyFlexibleSectionProductionHouseBlock
            v-if="section.__typename === 'flexibleSections_productionHouseBlock_BlockType'"
            :key="section.id"
            v-bind="section"
        />

        <LazyFlexibleSectionVenueBlock
            v-if="section.__typename === 'flexibleSections_venueBlock_BlockType'"
            :key="section.id"
            v-bind="section"
        />

        <LazyFlexibleSectionFestivalBlock
            v-if="section.__typename === 'flexibleSections_festivalBlock_BlockType'"
            :key="section.id"
            v-bind="section"
        />
    
        <LazyFlexibleSectionEventContentBlock
            v-if="section.__typename === 'flexibleSections_eventContentBlock_BlockType'"
            :key="section.id"
            v-bind="section"
        />

        <LazyNeverFlexibleSectionRichText
            v-if="section.__typename === 'flexibleSections_richText_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-never
        />

        <LazyIdleFlexibleSectionTheImage
            v-if="section.__typename === 'flexibleSections_image_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />

        <LazyIdleFlexibleSectionTheImages
            v-if="section.__typename === 'flexibleSections_images_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />

        <LazyIdleFlexibleSectionTheVideo
            v-if="section.__typename === 'flexibleSections_video_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />

        <LazyVisibleFlexibleSectionTheQuote
            v-if="section.__typename === 'flexibleSections_quote_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-visible
        />

        <LazyIdleFlexibleSectionCallToAction
            v-if="section.__typename === 'flexibleSections_callToAction_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />

        <LazyIdleFlexibleSectionTextImage
            v-if="section.__typename === 'flexibleSections_textImage_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />

        <LazyVisibleFlexibleSectionTextText
            v-if="section.__typename === 'flexibleSections_textText_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-visible
        />

        <LazyIdleFlexibleSectionTextVideo
            v-if="section.__typename === 'flexibleSections_textVideo_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />

        <LazyIdleFlexibleSectionSignupForm
            v-if="section.__typename === 'flexibleSections_signup_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />

        <LazyIdleFlexibleSectionInviteAFriend
            v-if="section.__typename === 'flexibleSections_inviteAFriend_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />

        <LazyIdleFlexibleSectionOrderGift
            v-if="section.__typename === 'flexibleSections_orderGift_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />

        <LazyIdleFlexibleSectionFeaturedCollection
            v-if="section.__typename === 'flexibleSections_featuredCollection_BlockType'"
            :key="section.id"
            v-bind="section"
            hydrate-on-idle
        />
    </template>

    <slot name="after" />
</template>

<script setup lang="ts">
import type { FlexibleSectionsFragment, Maybe } from '~/types/craft-schema';
import { RENDERCONDITION_LOGGED_IN_WITH_MEMBERSHIP, RENDERCONDITION_LOGGED_IN_WITHOUT_MEMBERSHIP, RENDERCONDITION_GUESTS } from '~/constants/renderConditions';

const props = defineProps<{
    sections: Maybe<FlexibleSectionsFragment>[]
}>();

const userStore = useUserStore();
const { loggedIn, user } = storeToRefs(userStore);
const { can } = userStore;

const hasMembership = computed(() => {
    return !!user.value?.subscriptions?.some((subscription) => {
        return ['normal', 'gift'].includes(subscription.type)
            && ['active', 'non_renewing', 'in_trial'].includes(subscription.status);
    });
});

const currentCondition = computed(() => {
    if (!loggedIn.value) {
        return RENDERCONDITION_GUESTS;
    }
    
    return hasMembership.value
        ? RENDERCONDITION_LOGGED_IN_WITH_MEMBERSHIP
        : RENDERCONDITION_LOGGED_IN_WITHOUT_MEMBERSHIP;
});

const shouldRender = (section: FlexibleSectionsFragment) => {
    if (!section.renderConditions?.includes(currentCondition.value)) {
        return false;
    }

    if (currentCondition.value === RENDERCONDITION_GUESTS) {
        return true;
    }

    // filter out the above render conditions so we only check entitlements
    const filteredRenderConditions = section.renderConditions.filter((v) =>
        ![RENDERCONDITION_LOGGED_IN_WITH_MEMBERSHIP, RENDERCONDITION_LOGGED_IN_WITHOUT_MEMBERSHIP, RENDERCONDITION_GUESTS].includes(v));

    // check ALL entitlements, if the array is empty, return true
    return filteredRenderConditions?.every((condition) => can(condition as Parameters<typeof can>[0])) ?? true;
};

const filteredSections = computed(() => props.sections.filter(section => section && shouldRender(section)) as FlexibleSectionsFragment[]);
</script>
